import { useCallback, useEffect, useRef, useState } from "react";
import { Box, Drawer, IconButton, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { LayoutDrawerSettingsState } from "./Layout";

const minWidth = 300;

const LayoutDrawer = ({ drawerSettings, onClose }: { drawerSettings: LayoutDrawerSettingsState; onClose: () => void }) => {

	const initialWidth = drawerSettings.size === "normal" ? 500 : 700;

	const [ width, setWidth ] = useState(initialWidth);
	const isResizing = useRef(false);

	const startResize = useCallback((e: React.MouseEvent) => {
		e.preventDefault();
		isResizing.current = true;
		document.body.style.cursor = "ew-resize";
		document.body.style.userSelect = "none";
	}, []);

	const resize = useCallback((e: MouseEvent) => {
		if (!isResizing.current) {
			return;
		}

		const currentMaxWidth = window.innerWidth * 0.9;
		const newWidth = window.innerWidth - e.clientX;

		if (newWidth >= minWidth && newWidth <= currentMaxWidth) {
			setWidth(newWidth);
		}
	}, []);

	const stopResize = useCallback(() => {
		if (isResizing.current) {
			isResizing.current = false;
			document.body.style.cursor = "";
			document.body.style.userSelect = "";
		}
	}, []);

	useEffect(() => {
		window.addEventListener("mousemove", resize);
		window.addEventListener("mouseup", stopResize);

		return () => {
			window.removeEventListener("mousemove", resize);
			window.removeEventListener("mouseup", stopResize);
		};
	}, [ resize, stopResize ]);

	useEffect(() => {
		setWidth(drawerSettings.size === "normal" ? 500 : 700);
	}, [ drawerSettings.size ]);

	return (
		<Drawer
			open={true}
			anchor="right"
			variant={drawerSettings.variant === "inlay" ? "persistent" : "temporary"}
			onClose={onClose}
			sx={{
				width: width,
				transition: isResizing.current ? "none" : "width 0.2s ease",
				pointerEvents:
					drawerSettings.variant === "inlay" || drawerSettings.variant === "overlayWithMask"
						? "auto"
						: "none",
			}}
			hideBackdrop={drawerSettings.variant === "overlayWithMask" ? false : true}
			slotProps={{
				paper: {
					sx: {
						backgroundColor: (theme) => theme.palette.mode === "dark" ? "#15171C" : "#F4F5F7",
						boxShadow: 0.5,
						height: "calc(100% - 50px)",
						pointerEvents: "auto",
						top: 50,
						transition: isResizing.current ? "none" : "width 0.2s ease",
						overflow: "visible",
						width: width,
					},
				},
			}}>
			<Box
				onMouseDown={startResize}
				sx={{
					backgroundColor: "transparent",
					bottom: 0,
					cursor: "ew-resize",
					left: -4,
					position: "absolute",
					top: 0,
					transition: "background-color 0.2s",
					width: 8,
					zIndex: 150,

					"&:hover": {
						backgroundColor: "primary.main",
						opacity: 0.5,
					},

					"&:active": {
						backgroundColor: "primary.main",
						opacity: 0.8,
					},
				}} />

			<Box
				sx={{
					display: "flex",
					height: "100%",
					flexDirection: "column",
					overflow: "hidden",
					width: "100%",
				}}>
				<Box
					sx={{
						flexGrow: 1,
						overflowY: "auto",
						position: "relative",
						width: "100%",
					}}>
					<Box
						sx={{
							alignItems: "center",
							borderBottom: "1px solid",
							borderColor: "divider",
							boxSizing: "border-box",
							display: "flex",
							justifyContent: "space-between",
							p: 2,
							width: "100%",
						}}>
						<Typography variant="h6" color="textPrimary" sx={{ fontSize: 20, fontWeight: 500 }}>
							{drawerSettings.title}
						</Typography>

						<IconButton onClick={onClose} size="small">
							<CloseIcon fontSize="small" />
						</IconButton>
					</Box>

					<Box sx={{ width: "100%" }}>
						{drawerSettings.content}
					</Box>
				</Box>
			</Box>
		</Drawer>
	);
};

export default LayoutDrawer;
